import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Boxes,
  Briefcase,
  Bug,
  Building2,
  ClipboardList,
  FileCheck,
  FileStack,
  FileText,
  FolderOpen,
  GitBranch,
  Globe,
  Handshake,
  Inbox,
  LayoutDashboard,
  Mail,
  Package,
  Scale,
  Settings,
  Shield,
  Sparkles,
  User,
  Users,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/utils/cn";
import { useAuth } from "@/contexts/AuthContext";
import { useBusinessUnit } from "@/hooks/useBusinessUnit";
import { unitUsesHouses } from "@/utils/unitHouses";
import { ADMIN_HEADER_HEIGHT_PX } from "@/components/layout/admin/adminChrome";
import hbkLogo from "@/assets/hbk-logo.png";

/** Which admin area's sidebar to render — driven by the route prefix, not the
 *  viewer's role (a super admin browsing /bu-admin sees the BU-admin rail, so
 *  the nav matches the area they are in — source shell behaviour). */
export type AdminArea = "super_admin" | "bu_admin" | "group_legal";

/**
 * **Every entry here has to lead somewhere a reader can act** (Jira NAV-86). An
 * entry that bounces them back, or opens a page that is permanently empty,
 * costs them the trip and teaches them the rail cannot be trusted — which is
 * worse than the entry being absent, because the next real entry gets the same
 * suspicion.
 *
 * `Sidebar.test.tsx` holds three parts of that: every address resolves, none of
 * them is an address that merely forwards, and every role a rail is rendered
 * for is admitted wherever it points. It cannot see the fourth — whether the
 * page behind an entry has anything on it — so a page that goes quietly empty
 * is still a matter of somebody noticing.
 *
 * **Two pages here have no entry on purpose.** `/admin/connectors` and
 * `/bu-admin/connectors` configure ticketing connectors and store credentials,
 * and nothing consumes either yet: per-request sync is deferred (PRD D9). They
 * stay routable so the work is not lost, and each gets its entry back with the
 * sync that gives it a point — which is why adding one now would be a
 * regression rather than a fix.
 */

/**
 * Role sidebar: a
 * dark-gradient rail with a logo + wordmark + role badge header, grouped nav
 * with lucide icons and a role-accent active state, and a footer of
 * portal-switch links. One component driven by the per-role config below.
 *
 * Admins keep a sidebar (design_guidelines.md) — unlike end users, whose nav
 * lives in the top navbar (Navbar.tsx). The end-user role has no sidebar.
 */

export interface NavItem {
  labelKey: string;
  to: string;
  icon: LucideIcon;
  /** For the unit's admin only (ADR 0025 decision 4): a BU admin who belongs
   *  to a house does not see it, because the rules would refuse every write
   *  behind it — imports, connectors, templates, contracts, policies,
   *  analytics and the houses themselves are the unit's. */
  unitOnly?: boolean;
  /** Only where the unit is organised into houses (Jira NAV-218): elsewhere
   *  the page behind it is empty and describes something the unit lacks. */
  needsHouses?: boolean;
  /** What this entry is called to a BU admin who belongs to a house. Only the
   *  entries whose *subject* changes with the scope need one: "BU Settings"
   *  leads to the house's own settings for them, and calling it the business
   *  unit's names the wrong thing — a house admin cannot reach the unit's. The
   *  rest ("Requests", "Users") already read correctly either way, because it
   *  is the same noun about a narrower set. */
  houseLabelKey?: string;
}

export interface FooterLink extends NavItem {
  /** "primary" renders a filled accent button (source's Super Admin CTA). */
  variant?: "primary" | "subtle" | "ghost";
}

export interface RoleConfig {
  /** i18n namespace holding the labels below. */
  ns: string;
  labelKey: string;
  /** The badge for a BU admin who belongs to a house: this rail is that
   *  house's, not the unit's, and the two are not the same authority (ADR 0025
   *  decision 4). Only the BU-admin rail has one — a house is a subdivision of
   *  a unit, so no other area can be entered from inside one. */
  houseLabelKey?: string;
  homeTo: string;
  /** Sidebar background gradient (tailwind classes). */
  gradient: string;
  /** Active nav item classes (bg + text). */
  activeItem: string;
  /** Active icon color. */
  activeIcon: string;
  /** Role badge classes. */
  badge: string;
  groups: NavItem[][];
  footer: FooterLink[];
}

const NAVY_GRADIENT = "bg-gradient-to-b from-[#1e2a3d] to-hb-navy-dark";

const SUPER_ADMIN_CONFIG: RoleConfig = {
  ns: "admin",
  labelKey: "label",
  homeTo: "/admin/dashboard",
  gradient: NAVY_GRADIENT,
  activeItem: "bg-hb-red/15 text-hb-red",
  activeIcon: "text-hb-red",
  badge: "text-hb-red bg-hb-red/15",
  groups: [
    [
      { labelKey: "groupDashboard", to: "/admin/dashboard", icon: LayoutDashboard },
      { labelKey: "businessUnits", to: "/admin/business-units", icon: Building2 },
      { labelKey: "groupCatalogue", to: "/admin/catalogue", icon: Briefcase },
      { labelKey: "toolsAcrossUnits", to: "/admin/tools", icon: Boxes },
      { labelKey: "categories", to: "/admin/categories", icon: FolderOpen },
      { labelKey: "groupContracts", to: "/admin/group-contracts", icon: Scale },
      { labelKey: "groupPolicies", to: "/admin/policies", icon: FileText },
    ],
    [
      { labelKey: "users", to: "/admin/users", icon: Users },
      { labelKey: "administrators", to: "/admin/administrators", icon: Shield },
    ],
    [
      { labelKey: "domains", to: "/admin/domains", icon: Globe },
      { labelKey: "analytics", to: "/admin/analytics", icon: BarChart3 },
      { labelKey: "auditLogs", to: "/admin/audit-logs", icon: ClipboardList },
      // Beside the audit trail rather than in the first group: both are places
      // the super admin goes to read what has happened, and neither is a thing
      // they configure (NAV-115).
      { labelKey: "bugReports", to: "/admin/bug-reports", icon: Bug },
      { labelKey: "webConfig", to: "/admin/web-config", icon: Settings },
      { labelKey: "settings", to: "/admin/settings", icon: Settings },
    ],
  ],
  footer: [
    {
      labelKey: "switchToBuAdmin",
      to: "/bu-admin/dashboard",
      icon: Building2,
      variant: "subtle",
    },
    { labelKey: "userView", to: "/end-user/dashboard", icon: User, variant: "ghost" },
  ],
};

const BU_ADMIN_CONFIG: RoleConfig = {
  ns: "buAdmin",
  labelKey: "label",
  houseLabelKey: "houseLabel",
  homeTo: "/bu-admin/dashboard",
  gradient: NAVY_GRADIENT,
  activeItem: "bg-amber-600/20 text-amber-400",
  activeIcon: "text-amber-400",
  badge: "text-amber-400 bg-amber-500/15",
  groups: [
    [
      { labelKey: "dashboard", to: "/bu-admin/dashboard", icon: LayoutDashboard },
      { labelKey: "toolManagement", to: "/bu-admin/tools", icon: Package },
      { labelKey: "requests", to: "/bu-admin/requests", icon: ClipboardList },
      { labelKey: "evaluationSteps.title", to: "/bu-admin/evaluation-steps", icon: GitBranch },
      { labelKey: "buPolicies", to: "/bu-admin/policies", icon: FileText, unitOnly: true },
    ],
    [
      { labelKey: "users", to: "/bu-admin/users", icon: Users },
      // The unit's houses (Jira NAV-96, ADR 0025): where a house is made to
      // exist and its domains are chosen. Beside the people, because that
      // is what a house is a division of.
      {
        labelKey: "housesNav",
        to: "/bu-admin/houses",
        icon: Building2,
        unitOnly: true,
        needsHouses: true,
      },
    ],
    [
      { labelKey: "groupContracts", to: "/bu-admin/group-contracts", icon: Scale, unitOnly: true },
    ],
    [
      { labelKey: "analytics", to: "/bu-admin/analytics", icon: BarChart3, unitOnly: true },
      { labelKey: "emailTemplates", to: "/bu-admin/email-templates", icon: Mail, unitOnly: true },
      {
        labelKey: "buSettings",
        houseLabelKey: "houseSettingsNav",
        to: "/bu-admin/settings",
        icon: Settings,
      },
    ],
  ],
  footer: [
    { labelKey: "userView", to: "/end-user/dashboard", icon: User, variant: "subtle" },
  ],
};

const GROUP_LEGAL_CONFIG: RoleConfig = {
  ns: "groupLegal",
  labelKey: "label",
  homeTo: "/group-legal/dashboard",
  gradient: "bg-gradient-to-b from-[#1a2e2b] to-[#0f1f1c]",
  activeItem: "bg-emerald-500/15 text-emerald-400",
  activeIcon: "text-emerald-400",
  badge: "text-emerald-400 bg-emerald-500/15",
  groups: [
    [
      { labelKey: "dashboard", to: "/group-legal/dashboard", icon: LayoutDashboard },
      // Every unit's undecided evaluations, with their legal steps and the
      // reader's own picked out before they are announced (Jira NAV-252).
      // Second, because it is the work with a clock on it (Jira NAV-258).
      { labelKey: "runningEvaluations", to: "/group-legal/evaluations", icon: Sparkles },
      // Where the cross-BU work actually starts: one row per product, with
      // what every business unit holds, what they concluded, and which products are
      // worth a contract (ADR 0016). The four status lists below are the
      // pipeline once a product is in it.
      { labelKey: "toolsAcrossGroup", to: "/group-legal/tools", icon: Boxes },
      // What a *business unit* has asked for, which is not a pipeline stage and had
      // nowhere to be worked (Jira NAV-78, ADR 0023): a request to join a
      // contract that already exists leaves that contract exactly where it is,
      // so it appeared on none of the four lists below and the notification
      // about it linked to one the tool is not on.
      { labelKey: "requests", to: "/group-legal/requests", icon: Inbox },
      { labelKey: "pendingReview", to: "/group-legal/pending", icon: FileCheck },
      { labelKey: "inNegotiation", to: "/group-legal/negotiation", icon: Handshake },
      { labelKey: "contracted", to: "/group-legal/contracted", icon: FileStack },
      { labelKey: "declined", to: "/group-legal/declined", icon: XCircle },
    ],
  ],
  footer: [
    { labelKey: "userView", to: "/end-user/dashboard", icon: User, variant: "ghost" },
  ],
};

/** Exported for `Sidebar.test.tsx`, which walks these entries against the route
 *  tree — the guard has to read the rails the app renders, not a copy. */
export const CONFIG_BY_AREA: Record<AdminArea, RoleConfig> = {
  super_admin: SUPER_ADMIN_CONFIG,
  bu_admin: BU_ADMIN_CONFIG,
  group_legal: GROUP_LEGAL_CONFIG,
};

const FOOTER_VARIANT: Record<NonNullable<FooterLink["variant"]>, string> = {
  primary: "bg-hb-red text-white hover-elevate",
  subtle: "glass-subtle text-white/70 hover-elevate",
  ghost: "text-white/50 hover-elevate",
};

interface SidebarProps {
  area: AdminArea;
  /** Where the rail is pinned: below the banners the root pins above an admin
   *  area (Jira NAV-222). Defaults to the top of the window. */
  top?: number;
}

/** The rail a reader gets: a BU admin who belongs to a house sees it without the
 *  unit's own entries (ADR 0025 decision 4), a unit that is not organised into
 *  houses has no Houses entry (Jira NAV-218), and a group that empties is
 *  dropped with its divider. Exported so the rule is testable without a
 *  router. */
export function visibleGroups(
  groups: NavItem[][],
  houseId: string | null,
  usesHouses: boolean,
): NavItem[][] {
  return groups
    .map((group) =>
      group.filter(
        (item) => !(item.unitOnly && houseId) && !(item.needsHouses && !usesHouses),
      ),
    )
    .filter((group) => group.length > 0);
}

export function Sidebar({ area, top = 0 }: SidebarProps) {
  const config = CONFIG_BY_AREA[area];
  const { houseId, businessUnitId } = useAuth();
  const { businessUnit } = useBusinessUnit(businessUnitId);
  const groups = visibleGroups(config.groups, houseId, unitUsesHouses(businessUnit));
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { t } = useTranslation(config.ns);

  const isActive = (to: string) =>
    pathname === to || pathname.startsWith(to + "/");

  return (
    // Pinned to the window below any banners, at the height they leave
    // (Jira NAV-28, Jira NAV-222), so the header stays level with the top bar
    // beside it and the footer's "User view" is always on screen. The nav
    // between them is what scrolls when a rail is longer than that, rather
    // than the page carrying the footer off the bottom. `self-start` because a
    // stretched flex child cannot stick.
    <aside
      className={cn(
        "sticky flex w-64 shrink-0 flex-col self-start border-r-0",
        config.gradient,
      )}
      style={{ top, height: `calc(100dvh - ${top}px)` }}
      data-testid="admin-sidebar"
    >
      {/* The top bar's height exactly, so the logo and the business unit's name
          sit on one line across the screen. */}
      <div
        className="flex shrink-0 items-center border-b border-white/10 px-5"
        style={{ height: ADMIN_HEADER_HEIGHT_PX }}
      >
        <Link to={config.homeTo} className="flex items-center gap-3">
          <img
            src={hbkLogo}
            alt="Holtzbrinck"
            className="h-9 w-9 shrink-0 object-contain"
          />
          <div className="flex flex-col">
            <span className="font-display text-lg font-semibold leading-tight text-white">
              {t("navigation:nav.navigator")}
            </span>
            <span
              className={cn(
                // `uppercase` here rather than in the copy (Jira NAV-155):
                // the capitals are a display treatment, and a label that
                // carries them cannot also be read as a sentence.
                "mt-0.5 inline-block w-fit rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                config.badge,
              )}
            >
              {t(houseId && config.houseLabelKey ? config.houseLabelKey : config.labelKey)}
            </span>
          </div>
        </Link>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto py-2">
        {groups.map((group, index) => (
          <div key={group[0].to}>
            {index > 0 && <div className="mx-3 my-2 h-px bg-white/10" />}
            <div className="flex flex-col gap-0.5 px-2">
              {group.map((item) => {
                const active = isActive(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-white/70 transition-colors hover:bg-white/5",
                      active && config.activeItem,
                    )}
                    data-testid={`nav-${item.to.split("/").pop()}`}
                  >
                    <item.icon
                      className={cn("h-4 w-4", active && config.activeIcon)}
                    />
                    <span>
                      {t(
                        houseId && item.houseLabelKey
                          ? item.houseLabelKey
                          : item.labelKey,
                      )}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="flex shrink-0 flex-col gap-1 border-t border-white/10 p-3">
        {config.footer.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              "flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium",
              FOOTER_VARIANT[item.variant ?? "ghost"],
            )}
            data-testid={`footer-${item.to.split("/").pop()}`}
          >
            <item.icon className="h-4 w-4" />
            <span>{t(item.labelKey)}</span>
          </Link>
        ))}
      </div>
    </aside>
  );
}
